<template>
  <SharedPublishingArticle
    :pageTitle="$t('components.sub.navigation.blog')"
    :allArticlesUrl="sectionRoot"
    :viewAllArticlesLink="$t('scenes.blog.view-all-posts-button')"
    :viewAllArticlesButton="$t('scenes.blog.view-all-posts')"
    :article="post"
    :author="author"
  />
</template>

<script>
  import urlJoin from 'url-join'

  import SharedPublishingArticle from '~/components/shared/publishing/article.vue'

  export default {
    name: 'BlogDetail',

    components: {
      SharedPublishingArticle,
    },

    async asyncData({ app, params, query, $axios, error, redirect }) {
      const endpointRoot = '/v2/blogs'
      const sectionRoot = '/blog'
      const blogsRoot = '/blog'

      let post = null
      let author = null
      let locales = null
      let cacheQuery = ''

      const queries = Object.keys(query)
      if (queries.length > 0 && queries.includes('clear_cache')) {
        cacheQuery = `?clear_cache${query.clear_cache ? '=' + query.clear_cache : ''}`
      }

      const URI = `${endpointRoot}/${params.title}${cacheQuery}`
      const encodedURI = encodeURI(URI)

      try {
        const response = await $axios.get(encodedURI)
        post = response.data
        author = post.author
        locales = post.locales
      } catch (e) {
        if (e.response?.data?.code === 'common.not-found') {
          error({
            statusCode: 404,
            message: e.response?.data?.message || '',
          })
        } else {
          error(e)
        }

        return
      }

      let postUrl = urlJoin(process.env.APP_URL || 'https://airalo.com', app.localePath(`${blogsRoot}/${post.slug}`))

      return { post, author, locales, postUrl, sectionRoot }
    },

    head() {
      const blogStructuredData = {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: this.post.title,
        author: {
          '@type': 'Organization',
          name: 'Airalo',
          logo: {
            '@type': 'ImageObject',
            url: 'https://airalo.com/logo.png',
          },
        },
        publisher: {
          '@type': 'Organization',
          name: 'Airalo',
          logo: {
            '@type': 'ImageObject',
            url: 'https://airalo.com/logo.png',
          },
        },
        image: this.post?.image?.url || '',
        url: this.postUrl,
        mainEntityOfPage: this.postUrl,
        datePublished: this.post.published_at,
        dateCreated: this.post.published_at,
        dateModified: this.post.published_at,
        description: this.post.excerpt,
      }

      const link = []

      if (this.locales?.length > 0) {
        if (this.locales.includes('en')) {
          const enURL = urlJoin(process.env.APP_URL, 'blog', this.post.slug)

          link.push({
            hid: 'i18n-xd',
            rel: 'alternate',
            href: enURL,
            hreflang: 'x-default',
          })
          link.push({
            hid: 'i18n-alt-en',
            rel: 'alternate',
            href: enURL,
            hreflang: 'en',
          })
        }
        this.locales.forEach((v) => {
          if (v !== 'en') {
            const langURL = urlJoin(process.env.APP_URL, v, 'blog', this.post.slug)
            link.push({
              hid: `i18n-xd-${v}`,
              rel: 'alternate',
              href: langURL,
              hreflang: v,
            })
          }
        })
      }

      return {
        title: `${this.post.title} - Airalo Blog`,
        meta: [
          {
            hid: 'description',
            name: 'description',
            content: this.post.excerpt,
          },
          {
            hid: 'og:title',
            property: 'og:title',
            content: this.post.title,
          },
          {
            hid: 'og:description',
            property: 'og:description',
            content: this.post.excerpt,
          },
          {
            hid: 'og:image',
            property: 'og:image',
            content: this.post?.image?.url || '',
          },
        ],
        script: [
          {
            innerHTML: JSON.stringify(blogStructuredData),
            type: 'application/ld+json',
          },
        ],
        __dangerouslyDisableSanitizers: ['script'],
        link,
      }
    },

    created() {
      this.$store.commit('set_mobile_header_text', '')
    },
  }
</script>

<style lang="scss" scoped>
  .blog-detail {
    &-container {
      background-color: #fff;
      background-clip: border-box;
      border: 1px solid rgba(0, 0, 0, 0.125);
      border-radius: 7px;
    }
  }

  .post-content {
    & ::v-deep {
      h2 {
        font-size: 1.25rem;
        font-weight: bold;
        margin-top: 1.5em;
        margin-bottom: 1em;
      }

      h3,
      h4,
      h5,
      h6 {
        font-size: 1.125rem;
        font-weight: bold;
        margin-top: 1.5em;
        margin-bottom: 1em;
      }

      ul {
        display: block;
        list-style: disc;
        padding-left: 1rem;
        margin-top: 1rem;
        margin-bottom: 1rem !important;
      }

      li {
        margin-top: 0.5rem;
      }

      img {
        display: block;
        max-width: 100%;
        height: auto;
      }

      a {
        color: #007bff;
        text-decoration: underline;
      }
    }
  }
</style>
